import { useCallback, useRef, useState } from 'react';

/**
 * Lote «ubicado» desde una card (⋮ → Ubicar en el mapa): centra el mapa y deja
 * la card marcada hasta que se vuelve a tocar, se ubica otro o se limpia.
 * Lo comparten Registros y el detalle de lista (propia y compartida conmigo).
 *
 * - `toggleLocatedLot(lotId, lot)`: si ya estaba ubicado lo quita (sin volver a
 *   centrar); si no, lo marca y llama `onLocate(lotId, lot)`.
 * - `clearLocatedLot()`: quita la marca (abrir ficha, cambiar de vista, etc.).
 * - `isLotLocated(lotId)`: para `locateActive` de la card.
 * - Vista por registro: `toggleLocatedLot(lotId, lot, recordKey)` marca solo esa
 *   card (`isRecordLocated(recordKey)`), aunque haya varias UF en el mismo lote.
 *   Otra card del mismo lote mueve la marca; la misma card la quita.
 *
 * El `lot` guardado sirve para seguir pintándolo en el mapa (Registros).
 */
export function useLocatedLot({ onLocate } = {}) {
  const [locatedLot, setLocatedLot] = useState(null);
  const onLocateRef = useRef(onLocate);
  onLocateRef.current = onLocate;
  const locatedLotIdRef = useRef(null);
  const [locatedRecordKey, setLocatedRecordKey] = useState(null);
  const locatedRecordKeyRef = useRef(null);
  locatedRecordKeyRef.current = locatedRecordKey;

  const locatedLotId = locatedLot?.lot_id != null && locatedLot.lot_id !== ''
    ? String(locatedLot.lot_id)
    : null;
  locatedLotIdRef.current = locatedLotId;

  const toggleLocatedLot = useCallback((lotId, lot, recordKey = null) => {
    const id = String(lotId || '');
    if (!id || !lot) return;
    const key = recordKey != null ? String(recordKey) : null;
    if (locatedLotIdRef.current === id && locatedRecordKeyRef.current === key) {
      locatedLotIdRef.current = null;
      locatedRecordKeyRef.current = null;
      setLocatedLot(null);
      setLocatedRecordKey(null);
      return;
    }
    const sameLot = locatedLotIdRef.current === id;
    locatedLotIdRef.current = id;
    locatedRecordKeyRef.current = key;
    setLocatedLot(lot);
    setLocatedRecordKey(key);
    // Otra card del mismo lote: el mapa ya está centrado y pintado.
    if (!sameLot) onLocateRef.current?.(lotId, lot);
  }, []);

  const clearLocatedLot = useCallback(() => {
    locatedLotIdRef.current = null;
    locatedRecordKeyRef.current = null;
    setLocatedLot(null);
    setLocatedRecordKey(null);
  }, []);

  const isRecordLocated = useCallback(
    (recordKey) => locatedRecordKey != null && recordKey != null && String(recordKey) === locatedRecordKey,
    [locatedRecordKey],
  );

  const isLotLocated = useCallback(
    (lotId) => locatedLotId != null && lotId != null && String(lotId) === locatedLotId,
    [locatedLotId],
  );

  return {
    locatedLot,
    locatedLotId,
    toggleLocatedLot,
    clearLocatedLot,
    isLotLocated,
    isRecordLocated,
  };
}
